"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { Menu as MenuIcon, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Drawer, DrawerContent, DrawerTrigger, DrawerClose, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
type HeaderLanguage = "en" | "it";
type MobileMenuConsumerProps = {
  language: HeaderLanguage;
};
function MobileMenuConsumer({
  language
}: MobileMenuConsumerProps) {
  useMenu();
  const t = {
    en: {
      home: "Home",
      details: "Wedding Day",
      travel: "Travel",
      registry: "Registry",
      rsvp: "RSVP",
      faq: "FAQ"
    },
    it: {
      home: "Home",
      details: "Matrimonio",
      travel: "Viaggio",
      registry: "Lista Nozze",
      rsvp: "RSVP",
      faq: "Domande"
    }
  }[language];
  const mobileItems = [{
    href: "/",
    label: t.home
  }, {
    href: "/wedding-day/",
    label: t.details
  }, {
    href: "/travel/",
    label: t.travel
  }, {
    href: "/registry/",
    label: t.registry
  }, {
    href: "/rsvp/",
    label: t.rsvp
  }, {
    href: "/faq/",
    label: t.faq
  }];
  return <nav className="flex flex-col items-center gap-8">
      {mobileItems.map(item => <Link key={item.href} to={item.href} className="text-3xl font-serif italic text-foreground hover:text-primary transition-colors">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [language, setLanguage] = useState<HeaderLanguage>("en");
  useEffect(() => {
    const savedLanguage = localStorage.getItem("site-language");
    if (savedLanguage === "en" || savedLanguage === "it") {
      setLanguage(savedLanguage);
    }
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const changeLanguage = (lang: HeaderLanguage) => {
    if (lang === language) return;
    localStorage.setItem("site-language", lang);
    setLanguage(lang);
    window.location.reload();
  };
  const t = {
    en: {
      home: "Home",
      details: "Wedding Day",
      travel: "Travel",
      registry: "Registry",
      rsvp: "RSVP",
      faq: "FAQ",
      english: "English",
      italian: "Italiano"
    },
    it: {
      home: "Home",
      details: "Matrimonio",
      travel: "Viaggio",
      registry: "Lista Nozze",
      rsvp: "RSVP",
      faq: "Domande",
      english: "English",
      italian: "Italiano"
    }
  }[language];
  const desktopTabClasses = "inline-flex items-center justify-center border border-primary px-4 xl:px-5 py-2 text-[10px] xl:text-[11px] font-sans uppercase tracking-[2.5px] xl:tracking-[3px] text-foreground/80 hover:bg-primary hover:text-primary-foreground transition-all duration-300 whitespace-nowrap";
  const languageButtonClasses = "flex items-center gap-2 text-[10px] sm:text-[11px] font-sans uppercase tracking-[2.5px] transition-colors duration-300 whitespace-nowrap";
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 border-b border-transparent", isScrolled ? "bg-background/80 backdrop-blur-md border-primary/10 shadow-sm" : "bg-background")}>
      <div className="mx-auto max-w-[1280px] px-6">
        <div className="hidden min-[1000px]:grid h-24 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-8">
          
          {/* LOGO */}
          <div className="flex justify-start">
            <Link to="/" className="block shrink-0">
              <img src="https://sara-michael-wedding.10web.cloud/wp-content/uploads/2026/04/download.png" alt="Michael&Sara" className="h-7 w-auto object-contain" />
            </Link>
          </div>

          {/* NAV */}
          <div className="flex justify-center min-w-0">
            <nav className="flex flex-wrap items-center justify-center gap-3 lg:gap-4 xl:gap-5">
              <Link to="/" className={desktopTabClasses}>{t.home}</Link>
              <Link to="/wedding-day/" className={desktopTabClasses}>{t.details}</Link>
              <Link to="/travel/" className={desktopTabClasses}>{t.travel}</Link>
              <Link to="/registry/" className={desktopTabClasses}>{t.registry}</Link>
              <Link to="/rsvp/" className={desktopTabClasses}>{t.rsvp}</Link>
              <Link to="/faq/" className={desktopTabClasses}>{t.faq}</Link>
            </nav>
          </div>

          {/* DESKTOP LANGUAGE */}
          <div className="flex justify-end">
            <div className="flex items-center gap-3 text-foreground/70">
              
              <button type="button" onClick={() => changeLanguage("en")} className={cn(languageButtonClasses, language === "en" ? "text-foreground" : "text-foreground/50 hover:text-foreground")}>
                <img src="https://saraandmichael2026.com/wp-content/uploads/2026/04/US-FLAG.png" alt="English" className="w-4 h-4 rounded-sm" />
                {t.english}
              </button>

              <span className="text-foreground/30">/</span>

              <button type="button" onClick={() => changeLanguage("it")} className={cn(languageButtonClasses, language === "it" ? "text-foreground" : "text-foreground/50 hover:text-foreground")}>
                <img src="https://saraandmichael2026.com/wp-content/uploads/2026/04/IT-FLAG.png" alt="Italiano" className="w-4 h-4 rounded-sm" />
                {t.italian}
              </button>

            </div>
          </div>
        </div>

        {/* MOBILE */}
        <div className="flex min-[1000px]:hidden h-20 items-center justify-between">
          <Link to="/" className="block relative z-50 shrink-0">
            <img src="https://sara-michael-wedding.10web.cloud/wp-content/uploads/2026/04/download.png" alt="Michael&Sara" className="h-7 w-auto object-contain" />
          </Link>

          <div className="flex items-center gap-4">
            
            {/* MOBILE LANGUAGE */}
            <div className="flex items-center gap-2 text-foreground/70">
              
              <button type="button" onClick={() => changeLanguage("en")} className={cn("flex items-center gap-1 text-[10px] font-sans uppercase tracking-[2px] transition-colors duration-300", language === "en" ? "text-foreground" : "text-foreground/50 hover:text-foreground")}>
                <img src="https://saraandmichael2026.com/wp-content/uploads/2026/04/US-FLAG.png" alt="EN" className="w-4 h-4 rounded-sm" />
                EN
              </button>

              <span className="text-foreground/30">/</span>

              <button type="button" onClick={() => changeLanguage("it")} className={cn("flex items-center gap-1 text-[10px] font-sans uppercase tracking-[2px] transition-colors duration-300", language === "it" ? "text-foreground" : "text-foreground/50 hover:text-foreground")}>
                <img src="https://saraandmichael2026.com/wp-content/uploads/2026/04/IT-FLAG.png" alt="IT" className="w-4 h-4 rounded-sm" />
                IT
              </button>

            </div>

            {/* DRAWER */}
            <SafeHydrate><Drawer>
              <DrawerTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-transparent">
                  <MenuIcon className="h-6 w-6 text-foreground" strokeWidth={1} />
                </Button>
              </DrawerTrigger>

              <DrawerContent className="h-[100dvh] mt-0 rounded-none flex flex-col bg-background">
                <div className="flex items-center justify-between p-6">
                  
                  {/* DRAWER LANGUAGE */}
                  <div className="flex items-center gap-3 text-foreground/70">
                    
                    <button type="button" onClick={() => changeLanguage("en")} className={cn("flex items-center gap-1 text-[10px] font-sans uppercase tracking-[2px]", language === "en" ? "text-foreground" : "text-foreground/50 hover:text-foreground")}>
                      <img src="https://saraandmichael2026.com/wp-content/uploads/2026/04/US-FLAG.png" alt="EN" className="w-4 h-4 rounded-sm" />
                      EN
                    </button>

                    <span className="text-foreground/30">/</span>

                    <button type="button" onClick={() => changeLanguage("it")} className={cn("flex items-center gap-1 text-[10px] font-sans uppercase tracking-[2px]", language === "it" ? "text-foreground" : "text-foreground/50 hover:text-foreground")}>
                      <img src="https://saraandmichael2026.com/wp-content/uploads/2026/04/IT-FLAG.png" alt="IT" className="w-4 h-4 rounded-sm" />
                      IT
                    </button>

                  </div>

                  <DrawerClose asChild>
                    <Button variant="ghost" size="icon" className="hover:bg-transparent">
                      <X className="h-6 w-6 text-foreground" />
                    </Button>
                  </DrawerClose>
                </div>

                <div className="flex-1 flex flex-col items-center justify-center p-6">
                  <MenuProvider menu_id="2" className="w-full">
                    <MobileMenuConsumer language={language} />
                  </MenuProvider>
                </div>
              </DrawerContent>
            </Drawer></SafeHydrate>

          </div>
        </div>
      </div>
    </header>;
}